import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import xss from "xss";
import classNames from "classnames";
import { FileText, Send, X } from "lucide-react";
import { backendLink } from "../config";
import MetaTags from "../components/MetaTagsIndex";
import { usePwaInstall } from "../components/hooks/usePwaInstall";
import { styles, ErrorBox, Field, PageShell, Select, ThanksScreen, cleanText, flagField, isValidEmail, localized, spacedPhone, toDigits } from "../components/wizard";

const MAX_MESSAGE = 1000;
const MAX_FILE_SIZE = 18 * 1024 * 1024; // 18MB
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
];

// Other requests (/contactez-nous): type, message, optional file, contact details
export default function OtherRequestForm({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { isStandalone } = usePwaInstall();
  const utmSource = useSelector((state) => state.utm.source); // ?utm= of the link (a channel id)

  // synchronize language switch
  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  const [types, setTypes] = useState([]);
  useEffect(() => {
    axios
      .get(`${backendLink}otherRequests/getOtherRequestTypes`)
      .then((response) => setTypes(Array.isArray(response.data?.data) ? response.data.data : []))
      .catch((error) => console.error("Error fetching request types:", error));
  }, []);

  const [typeId, setTypeId] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  // thumbnail of an added photo
  const filePreview = useMemo(() => (file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => filePreview && URL.revokeObjectURL(filePreview), [filePreview]);
  const [mail, setMail] = useState("");
  const [phone, setPhone] = useState("");
  const [fileError, setFileError] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [progress, setProgress] = useState(0); // % of the file uploaded, shown while sending

  const onFileChange = (e) => {
    const picked = e.target.files[0];
    e.target.value = "";
    if (!picked) return;
    if (!ALLOWED_FILE_TYPES.includes(picked.type)) return setFileError(t("odFileType"));
    if (picked.size > MAX_FILE_SIZE) return setFileError(t("odFileSize"));
    setFileError("");
    setFile(picked);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    const fail = (key) => {
      flagField(key);
      setError(t(key));
    };
    if (!typeId) return fail("odErrType");
    if (!message) return fail("odErrMsg");
    if (!phone) return fail("phone");
    if (mail !== "" && !isValidEmail(mail)) return fail("unvalidMail");

    const formData = new FormData();
    formData.append("type", typeId);
    formData.append("message", message);
    formData.append("phone", spacedPhone(phone));
    mail && formData.append("email", mail);
    file && formData.append("file", file);
    // as the complaints: 5 = the installed app, otherwise the link's channel, 1 by default.
    // The channel is also read from the session (kept by the home page): a reload of this page
    // empties the store. The backend takes a number only.
    const channel = utmSource || sessionStorage.getItem("utm_source");
    formData.append("channel_id", isStandalone ? 5 : /^\d+$/.test(channel || "") ? channel : 1);
    const deviceId = localStorage.getItem("push_device_id");
    if (deviceId) formData.append("device_id", deviceId);

    setError("");
    setProgress(0);
    setSending(true);
    try {
      await axios.post(`${backendLink}otherRequests/sendOtherRequest`, formData, {
        withCredentials: true, // Include cookies for CSRF protection
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded * 100) / e.total)),
      });
      setSent(true);
    } catch (err) {
      console.error("Error sending request:", err.response?.data || err);
      setError(t("suggestionErrorServer"));
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell
      formBg
      title={t("odTitle")}
      onBack={() => navigate("/")}
      currentLang={currentLang}
      setCurrentLang={setCurrentLang}
      done={sent && <ThanksScreen title={t("odOkTitle")} text={t("odOkText")} onHome={() => navigate("/")} />}
    >
      <MetaTags />
      <div className={styles.simpleBody}>
        <form noValidate onSubmit={onSubmit}>
          <Field label={t("odType")} required field="odErrType">
            <Select
              value={typeId}
              placeholder={t("odTypePh")}
              options={types.map((type) => ({ value: String(type.id), label: localized(type, lang) }))}
              onChange={setTypeId}
            />
          </Field>

          <Field label={t("odMsg")} required field="odErrMsg">
            <textarea
              className={styles.textarea}
              style={{ minHeight: 110 }}
              maxLength={MAX_MESSAGE}
              placeholder={t("odMsgPh")}
              value={message}
              // same filter as the previous form
              onChange={(e) => setMessage(xss(cleanText(e.target.value)))}
            />
            <span className={styles.count} style={{ display: "block" }}>
              {message.length} / {MAX_MESSAGE}
            </span>
          </Field>

          <div className={styles.field}>
            <label className={classNames(styles.ghost, { [styles.isOn]: file })} style={{ minHeight: 44, padding: "8px 12px", whiteSpace: "normal" }}>
              <FileText size={18} aria-hidden />
              <span>{t("odFile")}</span>
              <input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,.png,.jpg,.jpeg" onChange={onFileChange} />
            </label>
            {/* the added file is shown: a photo as its thumbnail, another file as its icon */}
            {file && (
              <div className={styles.fileTile}>
                <div className={styles.fileThumb}>
                  {filePreview ? (
                    <img src={filePreview} alt="" />
                  ) : (
                    <>
                      <FileText size={24} aria-hidden />
                      <small>{file.name.split(".").pop().toUpperCase()}</small>
                    </>
                  )}
                  <button type="button" className={styles.thumbRemove} aria-label={t("rcRemovePhoto")} onClick={() => setFile(null)}>
                    <X size={14} aria-hidden />
                  </button>
                </div>
                <span className={styles.fileName}>{file.name}</span>
              </div>
            )}
            {fileError && <div className={classNames(styles.hint, styles.isBad)}>{fileError}</div>}
          </div>

          <Field label={t("rcMail")} optional field="unvalidMail">
            <input
              type="email"
              className={classNames(styles.input, styles.ltr)}
              autoComplete="email"
              maxLength="64"
              value={mail}
              onChange={(e) => setMail(e.target.value.replace(/[^a-zA-Z0-9@._+-]/g, ""))}
            />
          </Field>
          <Field label={t("rcPhone")} required field="phone">
            <input
              type="tel"
              className={classNames(styles.input, styles.ltr)}
              autoComplete="tel"
              inputMode="numeric"
              placeholder="0555123456"
              value={phone}
              onChange={(e) => setPhone(toDigits(e.target.value).slice(0, 10))}
            />
          </Field>

          {/* a file can take a while to send: how much of it is uploaded, in the client's colour */}
          {sending && file && (
            <div className={styles.upload} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
              <div className={styles.uploadTrack}>
                <span className={styles.uploadBar} style={{ width: `${progress}%` }} />
              </div>
              <span className={styles.uploadText}>{progress < 100 ? t("odUploading", { n: progress }) : t("odUploadSaving")}</span>
            </div>
          )}
          <ErrorBox>{error}</ErrorBox>
          <button type="submit" className={classNames(styles.submit, { [styles.isSuccess]: sent })} disabled={sending || sent}>
            <Send size={17} strokeWidth={2.2} aria-hidden />
            <span>{sent ? t("rcSent") : t("odSend")}</span>
          </button>
        </form>
      </div>
    </PageShell>
  );
}
